import React from 'react';
import styled, { css } from 'styled-components';
import { useSelector, useDispatch } from 'react-redux';
import { header6 } from '@westwing/ui-kit/typography';
import ActiveOverlayPointerWrapper from '../../Overlays/ContentOverlay/ActiveOverlayPointerWrapper';
import HybridLink from '../../Generic/Links/HybridLink';
import Svg from '../../Generic/Icons';
import useConfig from '../../../hooks/useConfig';
import useTranslator, { BellaTranslator } from '../../../hooks/useTranslator';
import { Config } from '../../../../common/types/config';
import { phoneNumberToLink } from '../../../../common/helpers/phoneNumber';
import StateInterface from '../../../redux/types';
import { HelpCenterArticle } from '../../../redux/helpCenter/types';
import { fetchHelpCenterArticlesIfNeeded } from '../../../redux/helpCenter/actions';
import { VictorQaClasses } from '../../../../common/qaIds';

const Wrapper = styled(ActiveOverlayPointerWrapper)`
    padding: 20px 18px;
    width: 244px;
`;

export interface HelpCenterItemType {
    title: string;
    url: string;
    icon?: React.FC;
    border?: boolean;
}

const HelpCenterIcon = styled(Svg)`
    margin-right: 7px;
`;

const menuItems = (config: Config, t: BellaTranslator, articles: HelpCenterItemType[]): HelpCenterItemType[] => [
    ...articles,
    {
        title: t('hc_See all FAQ'),
        url: config.links.helpCenterUrl,
        border: true,
    },
    {
        title: t('Phone number'),
        url: phoneNumberToLink(config.hotline),
        border: false,
        icon: () => <HelpCenterIcon type="phone" />,
    },
    {
        title: t('hc_Email us'),
        url: config.links.helpCenterContactUrl,
        border: false,
        icon: () => <HelpCenterIcon type="envelope" />,
    },
];

const HelpCenterItem = styled(HybridLink)<{ border: boolean }>`
    padding: 12px 0 15px 0;
    color: ${({ theme }) => theme.colors.charcoal100};
    line-height: 1.4;
    align-items: center;
    display: flex;
    &:hover {
        text-decoration: underline;
    }
    &:first-child {
        padding-top: 0;
    }
    &:last-child {
        padding-bottom: 0;
    }
    ${({ border, theme }) =>
        border &&
        css`
            border-bottom: 1px solid ${theme.colors.charcoal20};
        `}

    ${({ border }) =>
        !border &&
        css`
            padding: 10px 0 0 0;
        `}
`;

const InnerText = styled.span<{ hasIcon: boolean }>`
    max-width: 163px;
    display: block;
    ${({ hasIcon }) =>
        hasIcon &&
        css`
            padding-bottom: 4px;
        `}
`;

const Title = styled.h6`
    ${header6}
    margin: 0 0 0 0;
    padding-bottom: 19px;
    cursor: auto;
    color: ${({ theme }) => theme.colors.charcoal100};
    ${({ theme }) => `border-bottom: 1px solid ${theme.colors.charcoal20};`}
`;

const HelpCenterContent: React.FC<{ fetchArticles?: boolean }> = ({ fetchArticles = true }) => {
    const config = useConfig();
    const t = useTranslator();
    const dispatch = useDispatch();

    const articles = useSelector<StateInterface, HelpCenterArticle[]>(state => state.helpCenter.articles);
    const articleItems = articles.map(a => ({
        ...a,
        border: true,
    }));

    if (fetchArticles) {
        dispatch(fetchHelpCenterArticlesIfNeeded());
    }

    return (
        <Wrapper data-testid="help-center-content" className={VictorQaClasses.headerHelpCenterFlyout}>
            <Title>{t('hc_How Can We Help You?')}</Title>
            {menuItems(config, t, articleItems).map(item => (
                <HelpCenterItem key={item.title} border={!!item.border} to={item.url}>
                    {item.icon && item.icon({})}
                    <InnerText hasIcon={!!item.icon}>{item.title}</InnerText>
                </HelpCenterItem>
            ))}
        </Wrapper>
    );
};

export default HelpCenterContent;
